import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import UsedPartOrderStatus from './partDetail/used/UsedPartOrderStatus';
import OrderTrackingSummary from './orderTracking/OrderTrackingSummary';
import {
  resolveTrackingState,
  STATE_LABELS,
  getTrackingBadgeClass,
  canRaiseClaim,
  canCancelOrder as canCancelTrackingOrder,
  canCancelInTransit,
} from '../utils/orderTrackingUtils';
import CancelOrderModal from './orderTracking/CancelOrderModal';
import ReassignmentOfferCard from './orderTracking/ReassignmentOfferCard';
import { orderTrackingService } from '../services/apiService';

import { 
  FaMapMarkerAlt, 
  FaCreditCard, 
  FaBox, 
  FaTruck, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaUndo,
  FaExclamationTriangle,
  FaClock,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaWarehouse,
  FaHome,
  FaPhoneAlt,
  FaDownload,
  FaSearch
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, USER_ROLES } from '../auth/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { recommendationService } from '../services/recommendationService';
import { formatPrice } from '../utils/currency';
import { toast } from 'react-toastify';
import Invoice from './Invoice';


export const MyOrder = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isCustomer } = useAuth();
  const { notifyOrderStatus, notifyReturn, notifyPayment } = useNotifications();
  const [activeTab, setActiveTab] = useState("In-Progress");
  const [orders, setOrders] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelFeeRequired, setCancelFeeRequired] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [orderToReturn, setOrderToReturn] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returnCustomReason, setReturnCustomReason] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTimeSlot, setPickupTimeSlot] = useState('');
  const [returning, setReturning] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState(null);
  const [showCancelItemsModal, setShowCancelItemsModal] = useState(false);
  const [orderToCancelItems, setOrderToCancelItems] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [cancelItemsReason, setCancelItemsReason] = useState('');
  const [cancelItemsCustomReason, setCancelItemsCustomReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const tabs = ["In-Progress", "Delivered", "Returned", "Cancelled"];

  const loadOrders = useCallback(async () => {
    try {
      const { orderService } = await import('../services/apiService');
      const response = await orderService.getOrders();
      const orders = response.orders || response.data?.orders || [];

      const transformedOrders = orders.map(order => ({
        id: order.orderId || order._id,
        date: order.createdAt || new Date().toISOString(),
        status: order.status,
        trackingState: order.trackingState,
        trackingTimeline: order.trackingTimeline,
        trackingUpdatedAt: order.trackingUpdatedAt,
        carrierTracking: order.carrierTracking,
        reassignment: order.reassignment,
        activeReassignmentId: order.activeReassignmentId,
        reassignmentExpiresAt: order.reassignmentExpiresAt,
        catalogNodeId: order.catalogNodeId,
        claimWindowEndsAt: order.claimWindowEndsAt,
        otpConfirmedAt: order.otpConfirmedAt,
        deliveryAutoConfirmedAt: order.deliveryAutoConfirmedAt,
        deliveryConfirmedAt: order.deliveryConfirmedAt,
        deliveredAt: order.deliveredAt || order.deliveredDate,
        usedPartMeta: order.usedPartMeta,
        bodyPartMeta: order.bodyPartMeta,
        items: order.items || [],
        cancelledItems: order.cancelledItems || [],
        packages: order.packages || [],
        shippingAddress: order.shippingAddress,
        paymentMethod: order.paymentMethod,
        paymentStatus: order.paymentStatus,
        paymentDetails: order.paymentDetails,
        subtotal: order.subtotal,
        deliveryCharge: order.deliveryCharge,
        platformFee: order.platformFee,
        total: order.total,
        totalItems: order.totalItems,
        cancellationReason: order.cancellationReason,
        cancelledDate: order.cancelledDate,
        returnStatus: order.returnStatus || null,
        returnData: order.returnStatus ? { reason: order.returnReason, items: order.returnItems || [], pickupDate: order.returnPickupDate, pickupTimeSlot: order.returnPickupTimeSlot } : null,
        returnInitiatedDate: order.returnInitiatedAt ? new Date(order.returnInitiatedAt).toISOString() : null,
        containsGarageServiceDeal: !!order.containsGarageServiceDeal,
        garageServiceNoReturnAccepted: !!order.garageServiceNoReturnAccepted,
        garageAdvanceForfeited: !!order.garageAdvanceForfeited,
        garageCancellationNote: order.garageCancellationNote || '',
        serviceSchedule: order.serviceSchedule || null,
      }));

      setOrders(transformedOrders);

      const focusOrderId = searchParams.get('orderId');
      if (focusOrderId) {
        const match = transformedOrders.find((o) => o.id === focusOrderId);
        if (match) {
          setActiveTab('In-Progress');
          setExpandedOrders({ [match.id]: true });
        }
      }

      transformedOrders.forEach(order => {
        recommendationService.trackOrder(order);
      });
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    }
  }, [searchParams]);

  useEffect(() => {
    loadOrders();
    const id = setInterval(loadOrders, 30000);
    return () => clearInterval(id);
  }, [loadOrders]);

  useEffect(() => {
    const raiseClaimOrderId = location?.state?.raiseClaimOrderId;
    if (raiseClaimOrderId) {
      navigate('/returns-disputes', {
        replace: true,
        state: { tab: 'raise', orderId: raiseClaimOrderId },
      });
    }
  }, [location?.state?.raiseClaimOrderId, navigate]);

  const goToReturnsDisputes = (orderId) => {
    navigate('/returns-disputes', {
      state: orderId ? { tab: 'raise', orderId } : { tab: 'raise' },
    });
  };

  // Toggle order details expansion
  const toggleOrderDetails = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Cancellation reasons
  const cancellationReasons = [
    { value: 'changed-mind', label: 'Changed my mind', icon: '🔄' },
    { value: 'found-cheaper', label: 'Found a better price elsewhere', icon: '💰' },
    { value: 'wrong-item', label: 'Ordered wrong item', icon: '❌' },
    { value: 'delivery-delay', label: 'Delivery taking too long', icon: '⏰' },
    { value: 'no-longer-needed', label: 'No longer needed', icon: '📦' },
    { value: 'payment-issue', label: 'Payment issue', icon: '💳' },
    { value: 'other', label: 'Other reason', icon: '📝' }
  ];

  const handleCancelOrder = (order) => {
    setOrderToCancel(order);
    setCancelFeeRequired(false);
    setShowCancelModal(true);
  };

  const confirmCancelOrder = async (reason, { payCancelFee = false } = {}) => {
    if (!orderToCancel || !reason) return;

    setCancelling(true);
    try {
      if (orderToCancel.containsGarageServiceDeal) {
        const { orderService } = await import('../services/apiService');
        await orderService.updateOrderStatus(orderToCancel.id, 'Cancelled', reason);
        toast.success('Order cancelled');
      } else {
        await orderTrackingService.cancelOrder(orderToCancel.id, reason, { payCancelFee });
        toast.success(
          payCancelFee
            ? 'Order cancelled — refund minus ₹100 cancel fee'
            : 'Order cancelled — refund initiated if paid'
        );
      }

      await loadOrders();
      await notifyOrderStatus(orderToCancel.id, 'Cancelled', orderToCancel);
      setShowCancelModal(false);
      setOrderToCancel(null);
      setCancelFeeRequired(false);
      if (activeTab !== 'Cancelled') setActiveTab('Cancelled');
    } catch (error) {
      if (error.code === 'CANCEL_FEE_REQUIRED') {
        setCancelFeeRequired(true);
        toast.warn(error.message || '₹100 cancel fee applies — dispatch photo already uploaded');
      } else {
        console.error('Failed to cancel order:', error);
        toast.error(error?.message || 'Failed to cancel order. Please try again.');
      }
    } finally {
      setCancelling(false);
    }
  };

  const handleInTransitCancel = async (order) => {
    if (
      !window.confirm(
        'Request cancellation while in transit? We will try to intercept with the carrier.'
      )
    ) {
      return;
    }

    setCancelling(true);
    try {
      await orderTrackingService.cancelInTransit(order.id, 'Buyer requested in-transit cancellation');
      await loadOrders();
      toast.success('Cancellation processed — refund initiated if intercept succeeded');
      if (activeTab !== 'Cancelled') setActiveTab('Cancelled');
    } catch (error) {
      toast.error(error?.message || 'Carrier could not intercept — you must receive the part');
    } finally {
      setCancelling(false);
    }
  };

  const handleCloseModal = () => {
    if (cancelling) return;
    setShowCancelModal(false);
    setCancelFeeRequired(false);
    setOrderToCancel(null);
  };

  const handleCancelSelectedItems = (order) => {
    setOrderToCancelItems(order);
    setSelectedProductIds([]);
    setCancelItemsReason('');
    setCancelItemsCustomReason('');
    setShowCancelItemsModal(true);
  };

  const toggleCancelItem = (productId) => {
    const pid = (productId || '').toString();
    setSelectedProductIds(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]);
  };

  const confirmCancelItems = async () => {
    if (!orderToCancelItems || selectedProductIds.length === 0) return;
    const reason = cancelItemsReason === 'other' ? cancelItemsCustomReason : cancellationReasons.find(r => r.value === cancelItemsReason)?.label || cancelItemsReason;
    if (!reason || (cancelItemsReason === 'other' && (!cancelItemsCustomReason || cancelItemsCustomReason.length < 10))) return;
    try {
      const { orderService } = await import('../services/apiService');
      const result = await orderService.cancelOrderItems(orderToCancelItems.id, selectedProductIds, reason);
      const updatedOrderFromApi = result?.order || result;
      const updatedOrders = orders.map(order => {
        if (order.id !== orderToCancelItems.id) return order;
        return {
          ...order,
          items: updatedOrderFromApi?.items ?? order.items,
          cancelledItems: updatedOrderFromApi?.cancelledItems ?? order.cancelledItems ?? [],
          subtotal: updatedOrderFromApi?.subtotal ?? order.subtotal,
          total: updatedOrderFromApi?.total ?? order.total,
          totalItems: updatedOrderFromApi?.totalItems ?? order.totalItems,
          status: updatedOrderFromApi?.status ?? order.status,
          cancellationReason: updatedOrderFromApi?.cancellationReason ?? order.cancellationReason,
          cancelledDate: updatedOrderFromApi?.cancelledDate ?? order.cancelledDate,
        };
      });
      setOrders(updatedOrders);
      setShowCancelItemsModal(false);
      setOrderToCancelItems(null);
      setSelectedProductIds([]);
      setCancelItemsReason('');
      setCancelItemsCustomReason('');
      toast.success('Selected items cancelled. Vendors and admin will see the reason.');
    } catch (error) {
      console.error('Failed to cancel items:', error);
      toast.error(error?.message || 'Failed to cancel items.');
    }
  };

  const normalizeOrderStatus = (status) => {
    const key = (status || '').toString().trim().toLowerCase();
    if (!key) return '';
    const aliases = {
      'in progress': 'in-progress',
      'in-progress': 'in-progress',
      'order placed': 'order placed',
      'pending payment': 'pending payment',
      confirmed: 'confirmed',
      processing: 'processing item',
      'processing item': 'processing item',
      packed: 'packed',
      'handed to courier': 'handed to courier',
      'shipment in transit': 'shipment in transit',
      delivered: 'delivery completed',
      'delivery completed': 'delivery completed',
      cancelled: 'cancelled',
      canceled: 'cancelled',
      returned: 'returned'
    };
    return aliases[key] || key;
  };

  const isCancelled = (o) => {
    const ts = resolveTrackingState(o);
    return ts === 'CANCELLED' || normalizeOrderStatus(o.status) === 'cancelled';
  };
  const isReturned = (o) => !!o.returnStatus || resolveTrackingState(o) === 'DISPUTED' || normalizeOrderStatus(o.status) === 'returned';
  const isDelivered = (o) => {
    const ts = resolveTrackingState(o);
    return (['DELIVERED', 'COMPLETED'].includes(ts) || normalizeOrderStatus(o.status) === 'delivery completed') && !o.returnStatus;
  };
  // Treat any non-cancelled/non-returned/non-delivered order as in-progress.
  const isInProgress = (o) => !isCancelled(o) && !isReturned(o) && !isDelivered(o);

  const filteredOrders = orders.filter(order => {
    // Filter by tab status
    let matchTab = false;
    if (activeTab === 'In-Progress') matchTab = isInProgress(order);
    else if (activeTab === 'Delivered') matchTab = isDelivered(order);
    else if (activeTab === 'Returned') matchTab = isReturned(order);
    else if (activeTab === 'Cancelled') matchTab = isCancelled(order);
    
    // Filter by search query (Order ID)
    const matchSearch = (order.id || '').toString().toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchTab && matchSearch;
  });

  const canCancelOrder = (order) => {
    if (order.containsGarageServiceDeal) {
      const cancellable = [
        'order placed',
        'pending payment',
        'confirmed',
        'processing item',
        'packed',
        'in-progress',
      ];
      return cancellable.includes(normalizeOrderStatus(order.status));
    }
    return canCancelTrackingOrder(order);
  };

  // Check if order can be returned (within 10 days of delivery) - Only for customers
  const canReturnOrder = (order) => {
    // Only allow returns for customers, not admins or other roles
    if (!isCustomer || user?.role !== USER_ROLES.CUSTOMER) {
      return false;
    }
    if (order.containsGarageServiceDeal) {
      return false;
    }
    if (order.status !== "Delivered" && order.status !== "Delivery Completed") {
      return false;
    }
    if (order.returnStatus) {
      return false; // Already has return initiated
    }
    const deliveryDate = order.deliveredDate ? new Date(order.deliveredDate) : new Date(order.date);
    const daysSinceDelivery = Math.floor((Date.now() - deliveryDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysSinceDelivery <= 10;
  };

  // Get days remaining for return
  const getReturnDaysRemaining = (order) => {
    const deliveryDate = order.deliveredDate ? new Date(order.deliveredDate) : new Date(order.date);
    const daysSinceDelivery = Math.floor((Date.now() - deliveryDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, 10 - daysSinceDelivery);
  };

  // Return reasons
  const returnReasons = [
    { value: 'defective', label: 'Defective/Damaged Product', icon: '🔧' },
    { value: 'wrong-item', label: 'Wrong Item Received', icon: '❌' },
    { value: 'not-as-described', label: 'Not as Described', icon: '📝' },
    { value: 'size-fit', label: 'Size/Fit Issue', icon: '📏' },
    { value: 'quality-issue', label: 'Quality Issue', icon: '⭐' },
    { value: 'changed-mind', label: 'Changed My Mind', icon: '🔄' },
    { value: 'other', label: 'Other Reason', icon: '📋' }
  ];

  // Pickup time slots
  const pickupTimeSlots = [
    '09:00 AM - 12:00 PM',
    '12:00 PM - 03:00 PM',
    '03:00 PM - 06:00 PM',
    '06:00 PM - 09:00 PM'
  ];

  // Handle return initiation - Only for customers
  const handleInitiateReturn = (order) => {
    // Double check: Only allow customers to initiate returns
    if (!isCustomer || user?.role !== USER_ROLES.CUSTOMER) {
      return;
    }
    setOrderToReturn(order);
    setReturnReason('');
    setReturnCustomReason('');
    setSelectedItems([]);
    setPickupDate('');
    setPickupTimeSlot('');
    setShowReturnModal(true);
  };

  // Toggle item selection for return
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Confirm return - Only for customers
  const confirmReturn = async () => {
    // Security check: Only allow customers to confirm returns
    if (!isCustomer || user?.role !== USER_ROLES.CUSTOMER) {
      return;
    }
    if (!orderToReturn || !returnReason || selectedItems.length === 0) {
      return;
    }
    if (returnReason === 'other' && (!returnCustomReason || returnCustomReason.length < 10)) {
      return;
    }
    if (!pickupDate || !pickupTimeSlot) {
      return;
    }

    setReturning(true);
    const reason = returnReason === 'other' 
      ? returnCustomReason 
      : returnReasons.find(r => r.value === returnReason)?.label || returnReason;

    try {
      const { orderService } = await import('../services/apiService');
      const returnData = {
        orderId: orderToReturn.id,
        items: selectedItems,
        reason: reason,
        pickupDate: pickupDate,
        pickupTimeSlot: pickupTimeSlot,
        returnStatus: 'Initiated'
      };
      
      const result = await orderService.initiateReturn(returnData);
      
      const returnStatus = result?.returnStatus ?? 'Initiated';
      const returnDataFromApi = result?.returnData ?? { reason, items: selectedItems, pickupDate, pickupTimeSlot };
      const returnInitiatedAt = result?.returnInitiatedAt ?? new Date();
      
      const updatedOrders = orders.map(order =>
        order.id === orderToReturn.id
          ? {
              ...order,
              returnStatus,
              returnData: returnDataFromApi,
              returnInitiatedDate: new Date(returnInitiatedAt).toISOString()
            }
          : order
      );
      setOrders(updatedOrders);
      
      await notifyReturn(orderToReturn.id, returnStatus);
      
      setShowReturnModal(false);
      setOrderToReturn(null);
      setReturnReason('');
      setReturnCustomReason('');
      setSelectedItems([]);
      setPickupDate('');
      setPickupTimeSlot('');
      
      if (activeTab !== 'Returned') {
        setActiveTab('Returned');
      }
    } catch (error) {
      console.error('Failed to initiate return:', error);
      setShowReturnModal(false);
      setOrderToReturn(null);
      setReturnReason('');
      setReturnCustomReason('');
      setSelectedItems([]);
      setPickupDate('');
      setPickupTimeSlot('');
    } finally {
      setReturning(false);
    }
  };

  // Get return status display - Only show for customers
  const getReturnStatusDisplay = (order) => {
    // Only show return status for customers
    if (!isCustomer || user?.role !== USER_ROLES.CUSTOMER) {
      return null;
    }
    if (!order.returnStatus) return null;
    
    const statusConfig = {
      'Initiated': { color: 'bg-blue-950/70 text-blue-200 border-blue-700', icon: <FaClock className="text-blue-400" />, label: 'Return Initiated' },
      'Approved': { color: 'bg-emerald-950/70 text-emerald-200 border-emerald-700', icon: <FaCheckCircle className="text-emerald-400" />, label: 'Return Approved' },
      'Pickup Scheduled': { color: 'bg-purple-950/70 text-purple-200 border-purple-700', icon: <FaCalendarAlt className="text-purple-400" />, label: 'Pickup Scheduled' },
      'Pickup Completed': { color: 'bg-indigo-950/70 text-indigo-200 border-indigo-700', icon: <FaTruck className="text-indigo-400" />, label: 'Pickup Completed' },
      'Refund Processing': { color: 'bg-yellow-950/60 text-yellow-200 border-yellow-700', icon: <FaSpinner className="text-yellow-400 animate-spin" />, label: 'Refund Processing' },
      'Refunded': { color: 'bg-emerald-950/70 text-emerald-200 border-emerald-700', icon: <FaMoneyBillWave className="text-emerald-400" />, label: 'Refunded' },
      'Rejected': { color: 'bg-red-950/70 text-red-200 border-red-800', icon: <FaTimesCircle className="text-red-400" />, label: 'Return Rejected' }
    };
    
    const config = statusConfig[order.returnStatus] || statusConfig['Initiated'];
    
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${config.color}`}>
        {config.icon}
        <span className="text-xs font-semibold">{config.label}</span>
      </div>
    );
  };

  // Get minimum pickup date (tomorrow)
  const getMinPickupDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Item status only when we have real data (no random simulation)
  const getItemStatus = (item, order) => {
    if (order.status === 'Cancelled' || order.status === 'Delivered') return null;
    if (item?.status === 'backordered') return { type: 'backordered', message: item.message || 'Item is backordered.' };
    if (item?.status === 'delayed') return { type: 'delayed', message: item.message || 'Delivery delayed.' };
    return null;
  };

  // Count orders by status
  const getOrderCount = (tab) => {
    if (tab === 'In-Progress') return orders.filter(isInProgress).length;
    if (tab === 'Delivered') return orders.filter(isDelivered).length;
    if (tab === 'Returned') return orders.filter(isReturned).length;
    if (tab === 'Cancelled') return orders.filter(isCancelled).length;
    return 0;
  };

  // Short order ID for display (avoid long MongoDB IDs)
  const getShortOrderId = (id) => {
    if (!id) return '—';
    const str = String(id);
    return str.length > 10 ? str.slice(-8) : str;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'In-Progress':
        return <FaBox className="text-blue-500" />;
      case 'Delivered':
        return <FaCheckCircle className="text-green-500" />;
      case 'Returned':
        return <FaUndo className="text-orange-500" />;
      case 'Cancelled':
        return <FaTimesCircle className="text-blue-500" />;
      default:
        return <FaBox className="text-slate-500" />;
    }
  };

  // Open Google Maps with address
  const handleOpenMap = (address) => {
    const fullAddress = `${address.address}, ${address.cityState}, ${address.postalCode}`;
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
    window.open(googleMapsUrl, '_blank');
  };

  return (
    <>
      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #0f172a;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #475569 0%, #334155 100%);
          border-radius: 10px;
          border: 1px solid #0f172a;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #64748b 0%, #475569 100%);
        }
      `}</style>
      <div
        className={`my-orders-dark w-full bg-[#0b1220] text-slate-100 ${
          embedded
            ? 'min-h-0 rounded-xl overflow-hidden pb-8 md:pb-6'
            : 'min-h-screen pb-20 md:pb-8'
        }`}
      >
      <div className="max-w-6xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-100">My Orders</h1>
            <p className="text-sm text-slate-400 mt-0.5">Track and manage your orders</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <button
              type="button"
              onClick={() => goToReturnsDisputes()}
              className="inline-flex items-center justify-center rounded-xl border border-amber-600/50 bg-amber-950/30 px-4 py-2.5 text-sm font-semibold text-amber-300 hover:bg-amber-950/50"
            >
              Returns &amp; Disputes
            </button>
          
          <div className="relative w-full md:w-72 lg:w-80 group">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-blue-500 transition-colors">
              <FaSearch size={14} />
            </div>
            <input
              type="text"
              placeholder="Search by Order ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#0d1425] border border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-200 outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/30 transition-all placeholder:text-slate-600"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
              >
                <FaTimes size={14} />
              </button>
            )}
          </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-slate-900/80 rounded-xl border border-slate-700 p-1.5 mb-6 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2.5 rounded-lg font-semibold text-sm whitespace-nowrap transition-all ${
                  activeTab === tab ? "bg-blue-600 text-white shadow-sm" : "text-slate-400 hover:bg-slate-800/90 hover:text-slate-200"
                }`}
              >
                {tab} <span className="opacity-80">({getOrderCount(tab)})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="empty-state flex flex-col items-center justify-center text-center py-16 md:py-24 bg-slate-900/90 rounded-2xl shadow-sm border border-slate-700"
          >
            <div className="empty-state-icon text-5xl md:text-6xl lg:text-6xl mb-3 md:mb-4">📦</div>
            <h3 className="text-lg md:text-xl lg:text-xl font-semibold text-slate-100 mb-1 md:mb-2">No {activeTab} orders</h3>
            <p className="text-slate-400 mb-4 md:mb-6 text-xs md:text-sm lg:text-sm max-w-md">
              You don't have any {activeTab.toLowerCase()} orders yet. Start shopping to see your orders here!
            </p>
            <Link
              to="/"
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 md:px-6 lg:px-6 py-2.5 md:py-3 lg:py-3 rounded-lg transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 text-xs md:text-sm lg:text-sm"
            >
              Continue Shopping
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-3 md:space-y-4 lg:space-y-4">
            {filteredOrders.map((order, idx) => {
              const trackingState = resolveTrackingState(order);
              const isExpanded = expandedOrders[order.id];
              
              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="order-card card-hover-effect bg-slate-900/90 rounded-xl md:rounded-2xl shadow-md hover:shadow-xl border border-slate-700 overflow-hidden transition-all duration-300"
                >
                  {/* Order card: compact header */}
                  <div className="p-4 md:p-5 border-b border-slate-700">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-300">
                          {getStatusIcon(order.status)}
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-base font-bold text-slate-100 truncate">
                            Order #{getShortOrderId(order.id)}
                          </h3>
                          <p className="text-sm text-slate-400 flex items-center gap-1">
                            <FaClock className="text-slate-500 flex-shrink-0" />
                            {formatDateShort(order.date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${getTrackingBadgeClass(trackingState)}`}>
                          {STATE_LABELS[trackingState]?.split('—')[0]?.trim() || trackingState}
                        </span>
                        <p className="text-lg font-bold text-slate-100">{formatPrice(order.total)}</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <OrderTrackingSummary order={order} expanded={isExpanded} />
                    </div>
                    {(trackingState === 'REASSIGNED' || isExpanded) && isInProgress(order) && (
                      <ReassignmentOfferCard orderId={order.id} onUpdated={loadOrders} />
                    )}
                    {order.returnStatus && (
                      <div className="mt-3">{getReturnStatusDisplay(order)}</div>
                    )}
                    <UsedPartOrderStatus order={order} />
                    {/* Item preview (collapsed): first 2 items */}
                    {!isExpanded && (order.items?.length > 0) && (
                      <div className="mt-4 space-y-2">
                        {(order.items || []).slice(0, 2).map((item, index) => (
                          <div key={index} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-slate-800/80 border border-slate-700">
                            <img loading="lazy"
                              src={item.imageUrl || 'https://via.placeholder.com/80'}
                              alt={item.name}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-slate-600"
                              onError={(e) => { e.target.src = 'https://via.placeholder.com/80'; }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-100 truncate">{item.name}</p>
                              <p className="text-xs text-slate-400">
                                Qty {item.quantity} × {formatPrice(item.discountPrice || item.price)}
                              </p>
                            </div>
                            <span className="text-sm font-semibold text-slate-100 flex-shrink-0">
                              {formatPrice((item.discountPrice || item.price) * item.quantity)}
                            </span>
                          </div>
                        ))}
                        {(order.items?.length || 0) > 2 && (
                          <p className="text-xs text-slate-500 pl-3">
                            +{(order.items?.length || 0) - 2} more item(s)
                          </p>
                        )}
                      </div>
                    )}
                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-700">
                      <button
                        onClick={() => toggleOrderDetails(order.id)}
                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                      >
                        {isExpanded ? <><FaChevronUp /> Hide details</> : <><FaChevronDown /> View details</>}
                      </button>
                      <button
                        onClick={() => { setSelectedOrderForInvoice(order); setShowInvoice(true); }}
                        className="px-3 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-1.5"
                      >
                        <FaCheckCircle className="text-green-600" /> Invoice
                      </button>
                      <button
                        onClick={() => navigate(`/orders/${order.id}/track`)}
                        className="px-3 py-2 text-sm font-medium text-blue-200 bg-blue-950/40 rounded-lg hover:bg-blue-950/60 flex items-center gap-1.5 border border-blue-700/40"
                      >
                        <FaTruck /> Track order
                      </button>
                      {canCancelOrder(order) && order.items?.length > 1 && (
                        <button onClick={() => handleCancelSelectedItems(order)} className="px-3 py-2 text-sm font-medium text-orange-300 bg-orange-950/50 rounded-lg hover:bg-orange-950/80 flex items-center gap-1.5">
                          <FaTimesCircle /> Cancel items
                        </button>
                      )}
                      {canCancelOrder(order) && (
                        <button
                          onClick={() => handleCancelOrder(order)}
                          disabled={cancelling}
                          className="px-3 py-2 text-sm font-medium text-red-300 bg-red-950/50 rounded-lg hover:bg-red-950/80 flex items-center gap-1.5 disabled:opacity-50"
                        >
                          <FaTimesCircle /> Cancel order
                        </button>
                      )}
                      {canCancelInTransit(order) && (
                        <button
                          onClick={() => handleInTransitCancel(order)}
                          disabled={cancelling}
                          className="px-3 py-2 text-sm font-medium text-orange-300 bg-orange-950/50 rounded-lg hover:bg-orange-950/80 flex items-center gap-1.5 disabled:opacity-50"
                        >
                          <FaTimesCircle /> Cancel in transit
                        </button>
                      )}
                      {canReturnOrder(order) && (
                        <button onClick={() => handleInitiateReturn(order)} className="px-3 py-2 text-sm font-medium text-orange-300 bg-orange-950/50 rounded-lg hover:bg-orange-950/80 flex items-center gap-1.5">
                          <FaUndo /> Return ({getReturnDaysRemaining(order)}d left)
                        </button>
                      )}
                      {canRaiseClaim(order) && (
                        <button
                          type="button"
                          onClick={() => goToReturnsDisputes(order.id)}
                          className="px-3 py-2 text-sm font-medium text-amber-300 bg-amber-950/40 rounded-lg hover:bg-amber-950/60 flex items-center gap-1.5 border border-amber-700/40"
                        >
                          <FaExclamationTriangle /> Raise claim
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Cancelled items - compact */}
                  {order.cancelledItems?.length > 0 && (
                    <div className="px-4 md:px-5 py-3 bg-red-950/40 border-b border-red-900/50">
                      <p className="text-xs font-semibold text-red-200 flex items-center gap-1.5">
                        <FaTimesCircle className="text-red-600" /> {order.cancelledItems.length} cancelled item(s)
                      </p>
                      <div className="mt-2 space-y-1.5">
                        {order.cancelledItems.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <img loading="lazy" src={item.imageUrl || 'https://via.placeholder.com/40'} alt="" className="w-8 h-8 rounded object-cover" onError={e => { e.target.src = 'https://via.placeholder.com/40'; }} />
                            <span className="text-slate-300 truncate flex-1">{item.name}</span>
                            <span className="text-red-600 font-medium shrink-0">Reason: {item.cancellationReason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Expanded: full items list */}
                  {isExpanded && (
                    <div className="p-4 md:p-5 border-b border-slate-700">
                      <h4 className="text-sm font-semibold text-slate-200 mb-3">Order items</h4>
                      <div className="space-y-2">
                        {(order.items || []).map((item, index) => {
                          const itemStatus = getItemStatus(item, order);
                          return (
                            <div
                              key={index}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                itemStatus ? 'bg-amber-950/40 border-amber-800' : 'bg-slate-800/60 border-slate-700'
                              }`}
                            >
                              <img loading="lazy"
                                src={item.imageUrl || 'https://via.placeholder.com/80'}
                                alt={item.name}
                                className="w-14 h-14 rounded-lg object-cover border border-slate-600 flex-shrink-0"
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/80'; }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-100 text-sm truncate">{item.name}</p>
                                <p className="text-xs text-slate-400 truncate">
                                  {[item.partNumber && `Part #${item.partNumber}`, item.brand, item.seller].filter(Boolean).join(' · ') || '—'}
                                </p>
                                {itemStatus && (
                                  <p className="text-xs text-amber-300 mt-0.5">{itemStatus.message}</p>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-xs text-slate-400">Qty {item.quantity}</p>
                                <p className="text-sm font-semibold text-slate-100">
                                  {formatPrice((item.discountPrice || item.price) * item.quantity)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Expanded: Delivery, payment & totals */}
                  {isExpanded && (
                    <div className="p-4 md:p-5 bg-slate-900/50 border-t border-slate-700 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {order.shippingAddress && (
                          <div className="bg-slate-800/90 p-4 rounded-xl border border-slate-700">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-slate-100 text-sm flex items-center gap-2">
                                <FaMapMarkerAlt className="text-blue-400" /> Delivery address
                              </h4>
                              <button
                                onClick={() => handleOpenMap(order.shippingAddress)}
                                className="text-xs text-blue-400 hover:text-blue-300 font-medium"
                              >
                                View map
                              </button>
                            </div>
                            <p className="text-sm text-slate-100 font-medium">{order.shippingAddress.name}</p>
                            <p className="text-xs text-slate-400">{order.shippingAddress.mobile}</p>
                            <p className="text-xs text-slate-400 mt-1">{order.shippingAddress.address}, {order.shippingAddress.cityState} {order.shippingAddress.postalCode}</p>
                          </div>
                        )}
                        <div className="bg-slate-800/90 p-4 rounded-xl border border-slate-700">
                          <h4 className="font-semibold text-slate-100 text-sm flex items-center gap-2 mb-2">
                            <FaCreditCard className="text-green-400" /> Payment
                          </h4>
                          <p className="text-sm text-slate-200">{order.paymentMethod}</p>
                          <p className="text-xs mt-1">
                            Status: <span className={order.paymentStatus === 'Paid' ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>{order.paymentStatus}</span>
                          </p>
                        </div>
                      </div>
                      <div className="bg-slate-800/90 p-4 rounded-xl border border-slate-700">
                        <h4 className="font-semibold text-slate-100 text-sm mb-3">Order summary</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between text-slate-400"><span>Subtotal ({order.totalItems} items)</span><span>{formatPrice(order.subtotal)}</span></div>
                          <div className="flex justify-between text-slate-400"><span>Delivery</span><span>{formatPrice(order.deliveryCharge)}</span></div>
                          <div className="flex justify-between text-slate-400"><span>Platform fee</span><span>{formatPrice(order.platformFee)}</span></div>
                          <div className="flex justify-between font-bold text-slate-100 pt-2 border-t border-slate-600 mt-2"><span>Total</span><span>{formatPrice(order.total)}</span></div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        <CancelOrderModal
          isOpen={showCancelModal}
          onClose={handleCloseModal}
          order={
            orderToCancel
              ? {
                  ...orderToCancel,
                  orderId: orderToCancel.id,
                  partName: orderToCancel.items?.[0]?.name,
                }
              : null
          }
          onConfirm={confirmCancelOrder}
          submitting={cancelling}
          cancelFeeRequired={cancelFeeRequired}
        />

        {/* Cancel selected items modal */}
        <AnimatePresence>
          {showCancelItemsModal && orderToCancelItems && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => { setShowCancelItemsModal(false); setOrderToCancelItems(null); }}>
                <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} onClick={e => e.stopPropagation()} className="bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full my-4 overflow-hidden border border-slate-700">
                  <div className="bg-slate-800 px-5 py-4 border-b border-slate-700">
                    <h3 className="text-lg font-bold text-slate-100">Cancel selected items</h3>
                    <p className="text-sm text-slate-400">Order #{orderToCancelItems?.id}. Reason will be shared with the vendor and admin.</p>
                  </div>
                  <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                    <p className="text-sm font-semibold text-slate-200 mb-2">Select items to cancel:</p>
                    <div className="space-y-2 mb-4">
                      {(orderToCancelItems?.items || []).map((item, idx) => (
                        <label key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-slate-600 hover:bg-slate-800/80 cursor-pointer">
                          <input type="checkbox" checked={selectedProductIds.includes((item.productId || '').toString())} onChange={() => toggleCancelItem(item.productId)} className="rounded border-slate-600" />
                          <img loading="lazy" src={item.imageUrl || 'https://via.placeholder.com/50'} alt="" className="w-10 h-10 object-cover rounded" onError={e => { e.target.src = 'https://via.placeholder.com/50'; }} />
                          <span className="text-sm font-medium text-slate-100 flex-1">{item.name}</span>
                          <span className="text-xs text-slate-400">Qty: {item.quantity}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-sm font-semibold text-slate-200 mb-2">Reason for cancellation (visible to vendor & admin):</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto mb-2">
                      {cancellationReasons.map(r => (
                        <button key={r.value} type="button" onClick={() => { setCancelItemsReason(r.value); if (r.value !== 'other') setCancelItemsCustomReason(''); }} className={`text-left p-2 rounded-lg border-2 text-xs font-medium ${cancelItemsReason === r.value ? 'border-orange-500 bg-orange-950/50 text-orange-200' : 'border-slate-600 text-slate-300'}`}>{r.label}</button>
                      ))}
                    </div>
                    {cancelItemsReason === 'other' && (
                      <textarea value={cancelItemsCustomReason} onChange={e => setCancelItemsCustomReason(e.target.value)} placeholder="Describe reason (min 10 chars)" className="w-full px-3 py-2 border rounded-lg text-sm mb-2" rows={2} />
                    )}
                  </div>
                  <div className="px-5 py-4 border-t border-slate-700 flex gap-2">
                    <button type="button" onClick={() => { setShowCancelItemsModal(false); setOrderToCancelItems(null); }} className="flex-1 py-2 border border-slate-600 rounded-lg font-medium text-slate-200 hover:bg-slate-800">Back</button>
                    <button type="button" onClick={confirmCancelItems} disabled={selectedProductIds.length === 0 || !cancelItemsReason || (cancelItemsReason === 'other' && (!cancelItemsCustomReason || cancelItemsCustomReason.length < 10))} className="flex-1 py-2 bg-orange-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed">Confirm cancel items</button>
                  </div>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Return Order Modal */}
        <AnimatePresence>
          {showReturnModal && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
                onClick={() => {
                  if (!returning) {
                    setShowReturnModal(false);
                    setOrderToReturn(null);
                    setReturnReason('');
                    setReturnCustomReason('');
                    setSelectedItems([]);
                    setPickupDate('');
                    setPickupTimeSlot('');
                  }
                }}
              >
                {/* Modal Container */}
                <motion.div
                  initial={{ scale: 0.95, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.95, opacity: 0, y: 20 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-slate-900 rounded-2xl md:rounded-xl shadow-2xl max-w-2xl md:max-w-3xl lg:max-w-4xl w-full my-4 md:my-8 overflow-hidden max-h-[90vh] flex flex-col border border-slate-700"
                >
                  {/* Header Section */}
                  <div className="bg-slate-800 px-4 md:px-5 lg:px-6 py-4 md:py-5 border-b border-slate-700 flex-shrink-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                        <div className="relative flex-shrink-0">
                          <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-900 rounded-xl md:rounded-lg shadow-lg flex items-center justify-center border-2 border-orange-700">
                            <FaUndo className="text-orange-400 text-xl md:text-2xl" />
                          </div>
                          <div className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-orange-500 rounded-full border-2 border-slate-900"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-slate-100 mb-1">
                            Initiate Return
                          </h3>
                          <div className="flex items-center gap-2 text-xs md:text-sm text-slate-400 flex-wrap">
                            <span className="font-medium">Order ID:</span>
                            <span className="font-mono bg-slate-800 px-2 py-0.5 rounded text-xs md:text-sm border border-slate-600 truncate max-w-[200px]">
                              #{orderToReturn?.id}
                            </span>
                            {orderToReturn && (
                              <span className="text-orange-400 font-semibold">
                                ({getReturnDaysRemaining(orderToReturn)} days remaining)
                              </span>
                            )}
                          </div>
                          {orderToReturn && (
                            <p className="text-xs text-slate-500 mt-1.5">
                              Delivered on {formatDate(orderToReturn.deliveredDate || orderToReturn.date)}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (!returning) {
                            setShowReturnModal(false);
                            setOrderToReturn(null);
                            setReturnReason('');
                            setReturnCustomReason('');
                            setSelectedItems([]);
                            setPickupDate('');
                            setPickupTimeSlot('');
                          }
                        }}
                        disabled={returning}
                        className="text-slate-400 hover:text-slate-200 transition-all p-1.5 md:p-2 hover:bg-slate-700 rounded-lg hover:rotate-90 flex-shrink-0 disabled:opacity-50"
                        aria-label="Close"
                      >
                        <FaTimes className="w-5 h-5 md:w-6 md:h-6" />
                      </button>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="px-4 md:px-5 lg:px-6 py-4 md:py-5 overflow-y-auto flex-1">
                    {/* Return Policy Info */}
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-blue-950/50 border-l-4 border-blue-500 rounded-lg md:rounded-xl p-3 md:p-4 mb-4 md:mb-5 shadow-sm"
                    >
                      <div className="flex items-start gap-2 md:gap-3">
                        <FaInfoCircle className="text-blue-400 text-lg md:text-xl flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-blue-100 mb-1 text-xs md:text-sm">
                            10-Day Return Policy
                          </h4>
                          <p className="text-xs md:text-sm text-blue-200 leading-relaxed">
                            You can return items within 10 days of delivery. Items must be unused, in original packaging with all tags and invoices.
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Select Items to Return */}
                    <div className="mb-4 md:mb-5">
                      <label className="block text-sm md:text-base font-bold text-slate-100 mb-3">
                        Select Items to Return <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                        {orderToReturn?.items?.map((item, index) => (
                          <motion.button
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => toggleItemSelection(item.id || index)}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                              selectedItems.includes(item.id || index)
                                ? 'border-orange-500 bg-orange-950/40'
                                : 'border-slate-600 bg-slate-800/80 hover:border-orange-600'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                selectedItems.includes(item.id || index)
                                  ? 'border-orange-500 bg-orange-500'
                                  : 'border-slate-500'
                              }`}>
                                {selectedItems.includes(item.id || index) && (
                                  <FaCheck className="text-white text-xs" />
                                )}
                              </div>
                              <img loading="lazy"
                                src={item.imageUrl || "https://via.placeholder.com/60"}
                                alt={item.name}
                                className="w-12 h-12 object-cover rounded flex-shrink-0"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/60?text=Product';
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-100 text-xs md:text-sm truncate">{item.name}</p>
                                <p className="text-xs text-slate-400">Qty: {item.quantity} × {formatPrice(item.discountPrice || item.price)}</p>
                              </div>
                            </div>
                          </motion.button>
                        ))}
                      </div>
                      {selectedItems.length === 0 && (
                        <p className="text-xs text-red-500 mt-2">Please select at least one item to return</p>
                      )}
                    </div>

                    {/* Return Reason Selection */}
                    <div className="mb-4 md:mb-5">
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <label className="block text-sm md:text-base font-bold text-slate-100">
                          Reason for Return
                        </label>
                        <span className="text-xs text-orange-300 font-semibold bg-orange-950/50 px-2 py-0.5 rounded">
                          Required *
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-2.5 max-h-64 md:max-h-72 overflow-y-auto pr-1 md:pr-2 custom-scrollbar">
                        {returnReasons.map((reason, index) => (
                          <motion.button
                            key={reason.value}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            onClick={() => {
                              setReturnReason(reason.value);
                              if (reason.value !== 'other') {
                                setReturnCustomReason('');
                              }
                            }}
                            className={`group relative w-full text-left p-3 md:p-3.5 rounded-lg md:rounded-xl border-2 transition-all duration-300 ${
                              returnReason === reason.value
                                ? 'border-orange-500 bg-orange-950/50 shadow-md md:scale-105'
                                : 'border-slate-600 bg-slate-800/80 hover:border-orange-600 hover:bg-slate-800 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-center gap-2 md:gap-3">
                              <div className={`w-8 h-8 md:w-9 md:h-9 rounded-lg flex items-center justify-center text-lg md:text-xl transition-all flex-shrink-0 ${
                                returnReason === reason.value
                                  ? 'bg-orange-500 text-white shadow-md'
                                  : 'bg-slate-700 text-slate-300 group-hover:bg-orange-950/80 group-hover:text-orange-300'
                              }`}>
                                {reason.icon}
                              </div>
                              <span className={`flex-1 font-semibold text-xs md:text-sm transition-colors line-clamp-2 ${
                                returnReason === reason.value ? 'text-orange-200' : 'text-slate-300 group-hover:text-orange-300'
                              }`}>
                                {reason.label}
                              </span>
                              {returnReason === reason.value && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="w-5 h-5 md:w-6 md:h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0"
                                >
                                  <FaCheck className="text-white text-xs md:text-sm" />
                                </motion.div>
                              )}
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Reason Input */}
                    {returnReason === 'other' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mb-4 md:mb-5"
                      >
                        <label className="block text-xs md:text-sm font-semibold text-slate-200 mb-2">
                          Please provide additional details
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <textarea
                          value={returnCustomReason}
                          onChange={(e) => setReturnCustomReason(e.target.value)}
                          placeholder="Please describe your reason..."
                          className={`w-full px-3 md:px-4 py-2.5 md:py-3 border-2 rounded-lg md:rounded-xl focus:ring-2 outline-none transition-all resize-none text-xs md:text-sm placeholder-gray-400 ${
                            returnCustomReason.length > 0 && returnCustomReason.length < 10
                              ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                              : 'border-slate-600 focus:border-orange-500 focus:ring-orange-900/40'
                          }`}
                          rows="3"
                        />
                        {returnCustomReason.length > 0 && returnCustomReason.length < 10 && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-xs text-red-500 mt-1.5 flex items-center gap-1"
                          >
                            <FaExclamationTriangle className="text-xs" />
                            Please provide at least 10 characters
                          </motion.p>
                        )}
                      </motion.div>
                    )}

                    {/* Pickup Date Selection */}
                    <div className="mb-4 md:mb-5">
                      <label className="block text-sm md:text-base font-bold text-slate-100 mb-2">
                        Preferred Pickup Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        min={getMinPickupDate()}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-slate-600 rounded-lg md:rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-900/40 outline-none text-xs md:text-sm bg-slate-900/80 text-slate-100"
                      />
                      {!pickupDate && (
                        <p className="text-xs text-red-500 mt-1">Please select a pickup date</p>
                      )}
                    </div>

                    {/* Pickup Time Slot Selection */}
                    <div className="mb-4 md:mb-5">
                      <label className="block text-sm md:text-base font-bold text-slate-100 mb-2">
                        Preferred Time Slot <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {pickupTimeSlots.map((slot) => (
                          <button
                            key={slot}
                            onClick={() => setPickupTimeSlot(slot)}
                            className={`p-3 rounded-lg border-2 transition-all text-xs md:text-sm ${
                              pickupTimeSlot === slot
                                ? 'border-orange-500 bg-orange-950/50 text-orange-200 font-semibold'
                                : 'border-slate-600 bg-slate-800/80 hover:border-orange-600 text-slate-300'
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                      {!pickupTimeSlot && (
                        <p className="text-xs text-red-500 mt-1">Please select a time slot</p>
                      )}
                    </div>

                    {/* Refund Information */}
                    {orderToReturn && selectedItems.length > 0 && (
                      <div className="bg-emerald-950/40 border border-emerald-800 rounded-lg md:rounded-xl p-3 md:p-4 mb-4 md:mb-5">
                        <h4 className="text-xs md:text-sm font-semibold text-emerald-200 mb-2 md:mb-3 flex items-center gap-2">
                          <FaMoneyBillWave className="text-emerald-400" />
                          Refund Information
                        </h4>
                        <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                          {orderToReturn.items
                            .filter(item => selectedItems.includes(item.id || orderToReturn.items.indexOf(item)))
                            .map((item, idx) => (
                              <div key={idx} className="flex justify-between text-slate-300">
                                <span>{item.name} (Qty: {item.quantity})</span>
                                <span className="font-medium">{formatPrice((item.discountPrice || item.price) * item.quantity)}</span>
                              </div>
                            ))}
                          <div className="pt-1.5 md:pt-2 border-t border-emerald-800 flex justify-between">
                            <span className="font-bold text-emerald-200">Total Refund Amount</span>
                            <span className="font-bold text-emerald-200">
                              ₹{orderToReturn.items
                                .filter(item => selectedItems.includes(item.id || orderToReturn.items.indexOf(item)))
                                .reduce((sum, item) => sum + ((item.discountPrice || item.price) * item.quantity), 0)
                                .toFixed(2)}
                            </span>
                          </div>
                          <p className="text-xs text-emerald-300 mt-2">
                            Refund will be processed within 5-7 business days after pickup verification.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer Section */}
                  <div className="bg-slate-800/90 px-4 md:px-5 lg:px-6 py-4 md:py-5 border-t border-slate-700 flex-shrink-0">
                    <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                      <button
                        onClick={() => {
                          if (!returning) {
                            setShowReturnModal(false);
                            setOrderToReturn(null);
                            setReturnReason('');
                            setReturnCustomReason('');
                            setSelectedItems([]);
                            setPickupDate('');
                            setPickupTimeSlot('');
                          }
                        }}
                        disabled={returning}
                        className="flex-1 px-4 md:px-6 py-2.5 md:py-3 bg-slate-900 border-2 border-slate-600 text-slate-200 rounded-lg md:rounded-xl hover:bg-slate-800 hover:border-slate-500 transition-all font-semibold text-xs md:text-sm shadow-sm hover:shadow-md disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmReturn}
                        disabled={returning || !returnReason || selectedItems.length === 0 || !pickupDate || !pickupTimeSlot || (returnReason === 'other' && (!returnCustomReason || returnCustomReason.length < 10))}
                        className={`flex-1 px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-semibold shadow-lg transition-all text-xs md:text-sm relative overflow-hidden flex items-center justify-center gap-2 ${
                          returning || !returnReason || selectedItems.length === 0 || !pickupDate || !pickupTimeSlot || (returnReason === 'other' && (!returnCustomReason || returnCustomReason.length < 10))
                            ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-orange-600 to-orange-700 text-white hover:from-orange-700 hover:to-orange-800 hover:shadow-xl md:transform md:hover:scale-[1.02] md:active:scale-[0.98]'
                        }`}
                      >
                        {returning ? (
                          <>
                            <FaSpinner className="animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <FaUndo />
                            Initiate Return
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 text-center mt-3 md:mt-4">
                      By confirming, you agree to return the selected items. Pickup will be scheduled as per your selection.
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
      </div>

      {/* Invoice Modal */}
      {showInvoice && selectedOrderForInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto border border-slate-700">
            <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate-100">Invoice</h2>
              <button
                onClick={() => {
                  setShowInvoice(false);
                  setSelectedOrderForInvoice(null);
                }}
                className="text-slate-400 hover:text-slate-200"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-6">
              <Invoice orderId={selectedOrderForInvoice.id} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
